// src/components/pages/CustomFunnelPaymentPage.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { PaymentOptions } from "../common/PaymentOptions";
import type { SplitInstallment } from "../common/PaymentOptions";
import { useInitializeLaunchKitNaijaMutation } from "../../app/api/customFunnelApi";

// ─── Pricing (kobo) ───────────────────────────────────────────────────────────
const FULL_PRICE_KOBO = 19789300; // ₦197,893
const SPLIT_1_KOBO = 8333300; // ₦83,333
const SPLIT_2_KOBO = 8333300;
const SPLIT_3_KOBO = 8333300;

const fmt = (kobo: number) =>
  "₦" + (kobo / 100).toLocaleString("en-NG", { minimumFractionDigits: 0 });

const SPLIT_INSTALLMENTS: SplitInstallment[] = [
  { amount: SPLIT_2_KOBO, dueLabel: "in 30 days" },
  { amount: SPLIT_3_KOBO, dueLabel: "in 60 days" },
];

const FEATURES = [
  "Automated sales pipeline",
  "Lead capture & intelligent scoring",
  "Email + WhatsApp follow-up sequences",
  "Real-time analytics dashboard",
  "Integrated Paystack payment collection",
  "AI-powered personalised follow-up",
  "Team performance tracking",
  "1-year free maintenance & support",
];

// ─── Success Screen ───────────────────────────────────────────────────────────

const SuccessScreen: React.FC<{
  firstName: string;
  email: string;
  amountKobo: number;
  whatsappRedirectUrl: string;
}> = ({ firstName, email, amountKobo, whatsappRedirectUrl }) => (
  <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4 py-16">
    <div className="text-center max-w-lg w-full">
      <div className="w-24 h-24 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center mx-auto mb-8 shadow-lg">
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
          <path
            d="M12 22L19 29L32 14"
            stroke="#16a34a"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <span className="inline-block px-3 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-full uppercase tracking-widest mb-4">
        Payment Confirmed
      </span>

      <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
        You're In, {firstName}!
      </h2>

      <p className="text-gray-600 text-base leading-relaxed mb-8">
        Your <strong className="text-gray-900">{fmt(amountKobo)}</strong>{" "}
        payment has been confirmed. A receipt and onboarding details have been
        sent to <strong className="text-gray-900">{email}</strong>.
      </p>

      <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8">
        <p className="text-gray-800 font-bold text-base mb-2">
          👇 Next Step — Join the WhatsApp Group
        </p>
        <p className="text-gray-600 text-sm mb-5">
          Click below to message us on WhatsApp. Your sales system will be live
          within 7 days.
        </p>
        <a
          href={whatsappRedirectUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors text-base"
        >
          💬 Message Us on WhatsApp
        </a>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-8 text-left">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">
          What Happens Next
        </p>
        {[
          "We receive your WhatsApp message and confirm your slot",
          "An onboarding call is scheduled within 24 hours",
          "Your domain, hosting, emails and Paystack are configured",
          "Your sales system goes live within 7 days 🚀",
        ].map((s, i, arr) => (
          <div
            key={i}
            className={`flex items-start gap-3 py-3 ${i < arr.length - 1 ? "border-b border-gray-100" : ""}`}
          >
            <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
              {i + 1}
            </div>
            <span className="text-gray-700 text-sm leading-relaxed">{s}</span>
          </div>
        ))}
      </div>

      <Link
        to="/"
        className="inline-flex items-center justify-center border-2 border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600 font-semibold px-8 py-3 rounded-full text-sm transition-colors"
      >
        ← Back to Homepage
      </Link>
    </div>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const CustomFunnelPaymentPage: React.FC = () => {
  // ── Mutation ─────────────────────────────────────────────────────────────
  const [initializeLaunchKitNaija, { isLoading: isInitializing }] =
    useInitializeLaunchKitNaijaMutation();

  // ── Post-submit state (only set after the API responds) ───────────────────
  const [step, setStep] = useState<"checkout" | "success">("checkout");
  const [confirmedFirstName, setConfirmedFirstName] = useState("");
  const [confirmedEmail, setConfirmedEmail] = useState("");
  const [confirmedAmount, setConfirmedAmount] = useState(FULL_PRICE_KOBO);
  const [whatsappRedirectUrl, setWhatsappRedirectUrl] = useState("");
  const [apiError, setApiError] = useState("");
  console.log(setStep);

  /**
   * handleSubmit — receives the consolidated payload from PaymentOptions
   * (name, email, phone, paymentPlan, amount). This is the only place the
   * API call lives; PaymentOptions owns the form and its own validation.
   */
  const handleSubmit = async (payload: {
    name: string;
    email: string;
    phone: string;
    paymentPlan: "full" | "split";
    amount: number; // kobo
  }) => {
    setApiError("");

    // Stash values needed by SuccessScreen / Paystack callback
    const firstName = payload.name.trim().split(/\s+/)[0] || payload.name;
    setConfirmedFirstName(firstName);
    setConfirmedEmail(payload.email);
    setConfirmedAmount(payload.amount);

    try {
      const amountNaira = payload.amount / 100; // server expects Naira

      const res = await initializeLaunchKitNaija({
        firstName,
        lastName: payload.name.trim().split(/\s+/).slice(1).join(" ") || "",
        email: payload.email,
        phoneNo: payload.phone,
        paymentPlan: payload.paymentPlan,
        product: {
          id: payload.paymentPlan === "split" ? "lkn_split" : "lkn_full",
          name: "Launch Kit Naija",
          price: fmt(payload.amount),
          priceNumeric: amountNaira,
        },
        metadata: {
          packageName: "Launch Kit Naija",
          packageId: payload.paymentPlan === "split" ? "lkn_split" : "lkn_full",
          phoneNo: payload.phone,
          paymentPlan: payload.paymentPlan,
        },
      }).unwrap();

      // authorizationUrl field name may vary — match WealthMethodCheckout.tsx pattern
      const authUrl =
        (res as any).authorization_url ||
        (res as any).authorizationUrl ||
        (res as any).authorizationURL;

      if (authUrl) {
        if (res.whatsappRedirectUrl) {
          sessionStorage.setItem(
            "lkn_whatsapp_redirect",
            res.whatsappRedirectUrl,
          );
          setWhatsappRedirectUrl(res.whatsappRedirectUrl);
        }
        // Full redirect to Paystack — Paystack calls back to /pay/success
        window.location.href = authUrl;
      } else {
        setApiError(
          (res as any).message ||
            "Failed to initialize payment. Please try again.",
        );
      }
    } catch (err: any) {
      setApiError(
        err?.data?.message ||
          err?.message ||
          "An error occurred. Please try again later.",
      );
    }
    // NOTE: do NOT set isInitializing to false manually — RTK Query handles it.
    // PaymentOptions reads isInitializing via the isSubmitting prop and resets
    // its own internal spinner automatically when the promise resolves.
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-white">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-extrabold tracking-tight">
            Launch Kit Naija
          </Link>
          <span className="flex items-center gap-2 text-sm text-blue-200">
            <span className="text-green-400 text-lg leading-none">●</span>
            Secure checkout
          </span>
        </div>
      </header>

      {/* ── Success Screen ───────────────────────────────────────────────────── */}
      {step === "success" ? (
        <SuccessScreen
          firstName={confirmedFirstName}
          email={confirmedEmail}
          amountKobo={confirmedAmount}
          whatsappRedirectUrl={whatsappRedirectUrl}
        />
      ) : (
        /* ── Checkout ─────────────────────────────────────────────────────── */
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Page header */}
            <div className="px-6 sm:px-12 pt-12 pb-8 text-center border-b border-gray-100">
              <div className="inline-block mb-4 px-4 py-1.5 bg-orange-50 border border-orange-200 rounded-full">
                <p className="text-sm text-orange-700 font-semibold">
                  ⚡ Only 4 Slots Remaining This Month
                </p>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 leading-tight">
                Complete Your Enrollment
              </h1>
              <p className="text-gray-600 max-w-xl mx-auto text-sm">
                You're one step away from launching your sales engine. Choose
                your plan, fill in your details, and proceed to Paystack.
              </p>
            </div>

            <div className="px-6 sm:px-12 py-10">
              {/* API error — shown above PaymentOptions if the initialize call fails */}
              {apiError && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-start gap-3">
                  <span className="text-red-500 text-lg flex-shrink-0 mt-0.5">
                    ⚠️
                  </span>
                  <p className="text-red-700 text-sm">{apiError}</p>
                </div>
              )}

              {/*
                PaymentOptions owns the plan cards AND the form.
                - isSubmitting is driven by RTK's isLoading so the button
                  stays locked while the Paystack initialize call is in flight.
                - onSubmit receives (name, email, phone, paymentPlan, amount)
                  and calls the API — no duplicate form fields needed here.
              */}
              <PaymentOptions
                fullPrice={FULL_PRICE_KOBO}
                splitFirst={SPLIT_1_KOBO}
                splitInstallments={SPLIT_INSTALLMENTS}
                features={FEATURES}
                initialSelected="full"
                labels={{
                  full: "Pay Once — Best Value",
                  split: "3-Part Instalment Plan",
                }}
                isSubmitting={isInitializing}
                onSubmit={handleSubmit}
              />
            </div>

            {/* Trust strip */}
            <div className="px-6 sm:px-12 pb-10">
              <div className="flex flex-wrap items-center justify-center gap-3 pt-6 border-t border-gray-100">
                {[
                  "🔒 256-bit SSL via Paystack",
                  "✓ 1-year maintenance included",
                  "✓ Setup starts within 24h",
                  "✓ WhatsApp onboarding after payment",
                ].map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-full px-4 py-1.5 text-xs text-gray-600"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
                <Lock size={12} />
                <span>Your information is secure and encrypted</span>
              </div>
            </div>
          </div>
        </main>
      )}

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer className="py-6 text-center text-sm text-gray-500 bg-white border-t mt-8">
        <div className="max-w-4xl mx-auto px-4">
          © {new Date().getFullYear()} Launch Kit Naija. All rights reserved.{" "}
          <a
            href="https://adeoluadegbemi.com"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-gray-700"
          >
            Privacy / Terms
          </a>
        </div>
      </footer>
    </div>
  );
};

export default CustomFunnelPaymentPage;
